// ** React Imports
import React, { Fragment, useEffect, memo, useRef } from 'react'

// ** Third Party Components
import classnames from 'classnames'

// ** Store & Actions
import { useSelector, useDispatch } from 'react-redux'
import {
  handleContentWidth,
  handleMenuCollapsed,
  handleMenuHidden
} from '@store/layout'

// ** ThemeConfig
import themeConfig from '@configs/themeConfig'

// ** Styles
import 'animate.css/animate.css'

const LayoutWrapper = ({ children, routeMeta }) => {
  // ** Store
  const dispatch = useDispatch()
  const navbar = useSelector((state) => state.navbar)
  const layout = useSelector((state) => state.layout)

  const {
    layout: layoutStored,
    contentWidth,
    menuCollapsed,
    menuHidden
  } = layout

  // ** Determine layout condition
  const appLayoutCondition =
    layoutStored === 'horizontal' &&
    (!routeMeta || (routeMeta && !routeMeta.appLayout))

  const Tag = appLayoutCondition ? 'div' : Fragment

  // ** Keep track of previous meta
  const prevRouteMeta = useRef(routeMeta)

  // ** Cleanup function to restore defaults
  const cleanUp = () => {
    if (!routeMeta) return

    const defaults = themeConfig.layout
    if (routeMeta.contentWidth && routeMeta.contentWidth === contentWidth) {
      dispatch(handleContentWidth(defaults.contentWidth))
    }
    if (routeMeta.menuCollapsed && routeMeta.menuCollapsed === menuCollapsed) {
      dispatch(handleMenuCollapsed(!menuCollapsed))
    }
    if (routeMeta.menuHidden && routeMeta.menuHidden === menuHidden) {
      dispatch(handleMenuHidden(!menuHidden))
    }
  }

  // ** Apply routeMeta layout settings only when they change
  useEffect(() => {
    if (!routeMeta) return

    const prevMeta = prevRouteMeta.current || {}

    if (routeMeta.contentWidth && routeMeta.contentWidth !== contentWidth) {
      dispatch(handleContentWidth(routeMeta.contentWidth))
    }
    if (
      routeMeta.menuCollapsed !== undefined &&
      routeMeta.menuCollapsed !== prevMeta.menuCollapsed
    ) {
      dispatch(handleMenuCollapsed(routeMeta.menuCollapsed))
    }
    if (
      routeMeta.menuHidden !== undefined &&
      routeMeta.menuHidden !== prevMeta.menuHidden
    ) {
      dispatch(handleMenuHidden(routeMeta.menuHidden))
    }

    prevRouteMeta.current = routeMeta
    return cleanUp
  }, [routeMeta])

  return (
    <div
      className={classnames('app-content content overflow-hidden', {
        [routeMeta?.className || '']: routeMeta?.className,
        'show-overlay': navbar.query?.length
      })}
    >
      <div className="content-overlay"></div>
      <div className="header-navbar-shadow" />
      <div
        className={classnames({
          'content-wrapper': !routeMeta?.appLayout,
          'content-area-wrapper': routeMeta?.appLayout
        })}
      >
        <Tag {...(appLayoutCondition ? { className: 'content-body' } : {})}>
          {children}
        </Tag>
      </div>
    </div>
  )
}

export default memo(LayoutWrapper)
